import {
  DayType,
  RecurrenceFormUnit,
  WeekDays,
  weekendDays,
  workWeek,
} from '@griply/types';

import { isNumber } from '../is-number';
import { ByWeekday, Frequency, Options, RRule } from '../lib/rrule';
import {
  getRruleWeekDays,
  parseByWeekdayWeekDays,
  parseRruleWeekDays,
} from './get-rrule-week-days';

export const parseIntervalFromRrule = (rrule: string) =>
  RRule.fromString(rrule).options.interval || 1;

// All set*InRrule helpers clone `origOptions` before mutating. The cached
// RRule instance returned by `RRule.fromString` is shared across callers
// (see ../lib/rrule/rrule.ts); mutating it corrupts subsequent reads —
// notably `parseWeekDaysFromRrule` would then see numeric byweekday entries
// instead of Weekday objects, propagate undefined to the next setter, and
// crash inside the library's parseOptions on `.n of undefined`.

// When all 7 week days are selected the rule collapses between FREQ=DAILY
// (interval 1) and FREQ=WEEKLY (interval > 1), since setWeekDaysInRrule
// represents "every day" as a bare FREQ=DAILY rule.
export const setIntervalInRrule = (rrule: string, interval: number): string => {
  const newInterval = interval || 1;
  const rule = RRule.fromString(rrule);
  const rruleWeekDays = parseWeekDaysFromRrule(rrule);
  const options: Partial<Options> = { ...rule.origOptions };

  if (rruleWeekDays.length === 7) {
    options.freq = newInterval > 1 ? Frequency.WEEKLY : Frequency.DAILY;
    options.byweekday =
      newInterval > 1 ? getRruleWeekDays(rruleWeekDays) : undefined;
  }
  options.interval = newInterval > 1 ? newInterval : undefined;

  return new RRule(options).toString();
};

export const parseFormUnitFromRrule = (rrule: string = '') => {
  const rule = RRule.fromString(rrule);

  switch (rule.origOptions.freq) {
    case RRule.YEARLY:
      return RecurrenceFormUnit.Years;
    case RRule.MONTHLY:
      return RecurrenceFormUnit.Months;
    case RRule.WEEKLY:
    case RRule.DAILY:
    default:
      return RecurrenceFormUnit.Weeks;
  }
};

export const parseWeekDaysFromRrule = (rrule: string = ''): WeekDays[] => {
  const rule = RRule.fromString(rrule);
  if (rule.origOptions.freq === Frequency.DAILY) {
    return Object.values(WeekDays);
  }

  return Array.isArray(rule.origOptions.byweekday)
    ? parseRruleWeekDays(rule.origOptions.byweekday)
    : [];
};

export const setWeekDaysInRrule = (rrule: string, weekDays: WeekDays[]) => {
  const rule = RRule.fromString(rrule);
  const options: Partial<Options> = {
    ...rule.origOptions,
    freq: Frequency.WEEKLY,
    bymonthday: undefined,
    bysetpos: undefined,
    bymonth: undefined,
    byweekday: getRruleWeekDays(weekDays),
  };

  if (
    weekDays.length === Object.values(WeekDays).length &&
    options.interval === 1
  ) {
    options.freq = Frequency.DAILY;
    options.byweekday = undefined;
  }

  return new RRule(options).toString();
};

export const parseMonthDaysFromRrule = (rrule: string = ''): number[] => {
  const rule = RRule.fromString(rrule);

  if (isNumber(rule.origOptions.bymonthday)) {
    return (rule.origOptions.bymonthday as number) > 0
      ? [rule.origOptions.bymonthday as number]
      : [];
  }

  return (rule.origOptions.bymonthday as number[]) ?? [];
};

export const setMonthDaysInRrule = (rrule: string, monthDays: number[]) => {
  const rule = RRule.fromString(rrule);
  const options: Partial<Options> = {
    ...rule.origOptions,
    freq: Frequency.MONTHLY,
    bymonthday: monthDays,
    bysetpos: undefined,
    byweekday: undefined,
  };

  return new RRule(options).toString();
};

export const parseMonthWeekDayFromRrule = (rrule: string = '') => {
  const rule = RRule.fromString(rrule);

  if (rule.origOptions.bymonthday === -1) {
    return {
      weekDay: DayType.Day,
      ordinal: (rule.origOptions.bysetpos as number) ?? -1,
    };
  }

  if (
    rule.origOptions.freq !== Frequency.MONTHLY ||
    !rule.origOptions.byweekday
  ) {
    return undefined;
  }

  const weekDays = Array.isArray(rule.origOptions.byweekday)
    ? rule.origOptions.byweekday
    : [];

  if (
    weekDays.length === workWeek.length &&
    parseRruleWeekDays(weekDays).every((weekDay) => workWeek.includes(weekDay))
  ) {
    return {
      weekDay: DayType.WorkDay,
      ordinal: rule.origOptions.bysetpos as number,
    };
  }

  if (
    weekDays.length === weekendDays.length &&
    parseRruleWeekDays(weekDays).every((weekDay) =>
      weekendDays.includes(weekDay),
    )
  ) {
    return {
      weekDay: DayType.WeekendDay,
      ordinal: rule.origOptions.bysetpos as number,
    };
  }

  const weekDay = weekDays[0] ?? rule.origOptions.byweekday;

  if (typeof weekDay !== 'object' || !('weekday' in weekDay)) {
    return undefined;
  }

  // Prefer bysetpos; fallback to the Weekday.n value (e.g., BYDAY=+2MO form)
  let ordinal = rule.origOptions.bysetpos as number;
  if (!isNumber(ordinal) && 'n' in weekDay) {
    const n = (weekDay as unknown as { n?: number }).n;
    if (isNumber(n)) {
      ordinal = n as number;
    }
  }

  return {
    weekDay: parseByWeekdayWeekDays([weekDay.weekday])[0],
    ordinal,
  };
};

export const setMonthSpecificDayInRrule = (
  rrule: string,
  {
    weekDay,
    ordinal,
  }: {
    weekDay: DayType | WeekDays;
    ordinal: number;
  },
) => {
  const rule = RRule.fromString(rrule);
  const options: Partial<Options> = {
    ...rule.origOptions,
    freq: Frequency.MONTHLY,
    bymonthday: undefined,
    byweekday: undefined,
    bysetpos: ordinal,
  };

  if (weekDay === DayType.Day) {
    options.bymonthday = [ordinal];
    options.bysetpos = undefined;
    return new RRule(options).toString();
  }

  options.byweekday = getRruleWeekDays([weekDay as WeekDays])?.[0];

  if (weekDay === DayType.WorkDay) {
    options.byweekday = getRruleWeekDays(workWeek);
  }

  if (weekDay === DayType.WeekendDay) {
    options.byweekday = getRruleWeekDays(weekendDays);
  }

  return new RRule(options).toString();
};

export const parseYearWeekDayFromRrule = (rrule: string = '') => {
  const rule = RRule.fromString(rrule);

  if (rule.origOptions.bymonthday) {
    const bymonthday = Array.isArray(rule.origOptions.bymonthday)
      ? rule.origOptions.bymonthday[0]
      : rule.origOptions.bymonthday;
    return {
      weekDay: DayType.Day,
      ordinal: (rule.origOptions.bysetpos as number) ?? (bymonthday as number),
    };
  }

  if (
    Frequency.YEARLY !== rule.origOptions.freq ||
    !rule.origOptions.byweekday
  ) {
    return undefined;
  }

  const weekDays = Array.isArray(rule.origOptions.byweekday)
    ? rule.origOptions.byweekday
    : [];

  if (
    weekDays.length === workWeek.length &&
    parseRruleWeekDays(weekDays).every((weekDay) => workWeek.includes(weekDay))
  ) {
    return {
      weekDay: DayType.WorkDay,
      ordinal: rule.origOptions.bysetpos as number,
    };
  }

  if (
    weekDays.length === weekendDays.length &&
    parseRruleWeekDays(weekDays).every((weekDay) =>
      weekendDays.includes(weekDay),
    )
  ) {
    return {
      weekDay: DayType.WeekendDay,
      ordinal: rule.origOptions.bysetpos as number,
    };
  }

  const weekDay = weekDays[0] ?? rule.origOptions.byweekday;

  if (typeof weekDay !== 'object' || !('weekday' in weekDay)) {
    return undefined;
  }

  return {
    weekDay: parseByWeekdayWeekDays([weekDay.weekday])[0],
    ordinal: rule.origOptions.bysetpos as number,
  };
};

export const setYearSpecificMonthDayInRrule = (
  rrule: string,
  {
    weekDay,
    ordinal,
    month,
  }: {
    weekDay: DayType | WeekDays;
    ordinal: number;
    month: number;
  },
) => {
  const rule = RRule.fromString(rrule);
  const options: Partial<Options> = {
    ...rule.origOptions,
    freq: Frequency.YEARLY,
    bymonthday: undefined,
    byweekday: undefined,
    bysetpos: ordinal,
    bymonth: month,
  };

  if (weekDay === DayType.Day) {
    options.bymonthday = [ordinal];
    options.bysetpos = undefined;
    return new RRule(options).toString();
  }

  options.byweekday = getRruleWeekDays([weekDay as WeekDays])?.[0];

  if (weekDay === DayType.WorkDay) {
    options.byweekday = getRruleWeekDays(workWeek);
  }

  if (weekDay === DayType.WeekendDay) {
    options.byweekday = getRruleWeekDays(weekendDays);
  }

  return new RRule(options).toString();
};

export const parseMonthFromRrule = (rrule: string): number | undefined => {
  const rule = RRule.fromString(rrule);
  return rule.options.bymonth?.[0];
};

export const isValidRrule = (
  rrule: string = '',
  { hasCount }: { hasCount?: boolean } = {},
) => {
  if (!rrule) {
    return false;
  }

  const rule = RRule.fromString(rrule);
  if (
    ![
      Frequency.DAILY,
      Frequency.WEEKLY,
      Frequency.MONTHLY,
      Frequency.YEARLY,
    ].includes(rule.origOptions.freq!)
  ) {
    return false;
  }

  const hasNoOptions = [
    rule.origOptions.byweekday,
    rule.origOptions.bymonthday,
    rule.origOptions.bysetpos,
  ].every((option) => option === undefined);

  if (hasCount && hasNoOptions) {
    return true;
  }

  switch (rule.origOptions.freq) {
    case Frequency.DAILY:
      return hasNoOptions;
    case Frequency.WEEKLY:
      return !!rule.origOptions.byweekday;
    case Frequency.MONTHLY:
      return (
        !!rule.origOptions.bymonthday ||
        (!!rule.origOptions.byweekday &&
          (!!rule.origOptions.bysetpos ||
          Array.isArray(rule.origOptions.byweekday)
            ? 'n' in
              ((rule.origOptions.byweekday as Array<ByWeekday>)[0] as object)
            : 'n' in (rule.origOptions.byweekday as object)))
      );
    case Frequency.YEARLY:
      return (
        !!rule.origOptions.byweekday ||
        !!(rule.origOptions.bymonth && rule.origOptions.bymonthday)
      );
    default:
      return false;
  }
};
